/**
 * HQ — 구독 동의 현황 (2026-10-27 대표 지시)
 *
 * 매장별로 새 이용약관·요금(2026-11-01 시행)에 동의·신청했는지 본다.
 * 2026-10-28 대표 지시: 베이직 정가 월 500,000원, 기존 매장은 프로모션 300,000원 — [확인] 때 할인을 같이 정한다.
 * 2026-10-27 23시 대표 결정: 원장이 설정 → 시설 정보에서 [베이직 신청] → 여기서 [확인](상태 기록만, 청구 없음).
 * 🔴 미동의·미신청 매장을 정지하는 버튼은 만들지 않는다(대표 결정: 직접 연락 후 수동 처리).
 */
import React, { useCallback, useEffect, useState } from 'react';
import hqSubscriptionConsentService, {
  SubscriptionConsentList,
  SubscriptionConsentStore,
} from '../services/hqSubscriptionConsentService';
import HqPriceInput, { HqPriceValue, priceToBody, priceToValue } from '../components/HqPriceInput';

const fmtKst = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

/** 상태 칸 — 신청이 먼저, 그다음 동의 */
const StatusCell: React.FC<{ s: SubscriptionConsentStore }> = ({ s }) => {
  if (s.excluded_reason) return <span className="text-slate-400">대상 아님 · {s.excluded_reason}</span>;
  if (s.request_status === 'confirmed') return <span className="text-emerald-600 font-semibold">✅ 베이직 신청 · 확인됨</span>;
  if (s.request_status === 'requested') return <span className="text-blue-600 font-bold">📝 베이직 신청함 · 확인 대기</span>;
  if (s.agreed) return <span className="text-amber-600 font-semibold">동의함(신청 전)</span>;
  return <span className="text-red-600 font-bold">⚠️ 미신청</span>;
};

const HqSubscriptionConsentsPage: React.FC = () => {
  const [data, setData] = useState<SubscriptionConsentList | null>(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [priceInput, setPriceInput] = useState<HqPriceValue>(priceToValue(null));

  const load = useCallback(() => {
    hqSubscriptionConsentService
      .list()
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || '불러오지 못했습니다'));
  }, []);

  useEffect(load, [load]);

  const openConfirm = (s: SubscriptionConsentStore) => {
    setOpenId(s.organization_id);
    setActionError('');
    setPriceInput(priceToValue(s.price));
  };

  const confirm = async (orgId: string) => {
    const { body, error: priceError } = priceToBody(priceInput);
    if (!body) {
      setActionError(priceError || '요금을 확인해 주세요');
      return;
    }
    setConfirming(orgId);
    setActionError('');
    try {
      await hqSubscriptionConsentService.confirm(orgId, body);
      setOpenId(null);
      load();
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || '확인 처리하지 못했습니다');
    } finally {
      setConfirming(null);
    }
  };

  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6 text-slate-500">불러오는 중…</div>;

  const { summary } = data;

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <h1 className="text-xl sm:text-2xl font-bold mb-1">📝 구독 동의 현황</h1>
      <p className="text-xs text-slate-500 mb-4">
        {data.effective_date} 시행 · {data.plan_name} 정가 {data.fee_label} · 약관 버전 {data.version}
        {data.previous_versions?.length ? ` (이전 판 ${data.previous_versions.join(', ')} 동의도 완료로 봅니다 — 다시 동의받지 않음)` : ''}
        <br />
        기존 매장은 따로 정하지 않으면 '{data.default_promo_label || '수강생 프로모션'}' 월 {(data.existing_promo_fee || 300000).toLocaleString('ko-KR')}원으로 보입니다. [확인] 때 매장별 할인을 정할 수 있습니다.
        <br />
        원장이 로그인하면 '설정 → 시설 정보에서 베이직 신청' 안내 창이 뜹니다(신청하면 더 안 뜸).
        신청하면 대표 텔레그램으로 알림이 갑니다 — 확인한 뒤 [확인]을 눌러 주세요(상태 기록만, 청구는 아직 안 함).
        미신청 매장은 자동으로 막지 않습니다 — 연락 후 직접 처리해 주세요.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">대상</div>
          <div className="text-lg sm:text-xl font-bold">{summary.targets}곳</div>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">베이직 신청</div>
          <div className="text-lg sm:text-xl font-bold text-emerald-600">{summary.requested}곳</div>
          {summary.awaiting_confirm > 0 && (
            <div className="text-xs text-blue-600 font-semibold">확인 대기 {summary.awaiting_confirm}곳</div>
          )}
        </div>
        <div className="bg-white rounded-xl border-2 border-red-300 p-3">
          <div className="text-xs text-slate-500">미신청</div>
          <div className="text-lg sm:text-xl font-bold text-red-600">{summary.not_requested}곳</div>
          <div className="text-xs text-slate-400">그중 동의만 함 {Math.max(0, summary.agreed - summary.requested)}곳</div>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">대상 아님</div>
          <div className="text-lg sm:text-xl font-bold text-slate-500">{summary.excluded}곳</div>
        </div>
      </div>

      {actionError && <p className="mb-3 text-sm text-red-600">{actionError}</p>}

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">매장</th>
              <th className="px-3 py-2 text-left">상태</th>
              <th className="px-3 py-2 text-left">요금(월, 부가세 별도)</th>
              <th className="px-3 py-2 text-left">베이직 신청 (KST)</th>
              <th className="px-3 py-2 text-left">동의자</th>
              <th className="px-3 py-2 text-left">동의 시각 (KST)</th>
              <th className="px-3 py-2 text-left">경로 · IP</th>
              <th className="px-3 py-2 text-left">원장 연락처</th>
            </tr>
          </thead>
          <tbody>
            {data.stores.map((s) => {
              const pending = !s.excluded_reason && !s.request_status;
              return (
                <tr
                  key={s.organization_id}
                  className={`border-t ${pending ? 'bg-red-50' : s.excluded_reason ? 'text-slate-400' : ''}`}
                >
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{s.organization_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <StatusCell s={s} />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {s.excluded_reason || !s.price ? (
                      '-'
                    ) : s.price.discount_amount ? (
                      <>
                        <div className="text-slate-400 line-through">{s.price.list_price.toLocaleString('ko-KR')}원</div>
                        <div className="text-rose-600">
                          {s.price.discount_label} −{s.price.discount_amount.toLocaleString('ko-KR')}
                        </div>
                        <div className="font-semibold">{s.price.monthly_fee.toLocaleString('ko-KR')}원</div>
                      </>
                    ) : (
                      <div className="font-semibold">{s.price.monthly_fee.toLocaleString('ko-KR')}원</div>
                    )}
                    {s.price && !s.price.is_custom && !s.excluded_reason && <div className="text-slate-400">(기본값)</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {s.request_status ? (
                      <>
                        <div>{fmtKst(s.requested_at)}</div>
                        {s.requested_by_name && <div className="text-slate-400">{s.requested_by_name}</div>}
                        {s.request_status === 'confirmed' ? (
                          <div className="text-emerald-600">확인 {fmtKst(s.confirmed_at)}</div>
                        ) : openId === s.organization_id ? (
                          <div className="mt-2 w-[26rem] max-w-[80vw] whitespace-normal">
                            <p className="mb-1 text-slate-600">이 매장 구독 요금을 정하고 확인합니다.</p>
                            <HqPriceInput value={priceInput} onChange={setPriceInput} compact />
                            <div className="mt-2 flex gap-2">
                              <button
                                type="button"
                                onClick={() => confirm(s.organization_id)}
                                disabled={confirming === s.organization_id}
                                className="px-3 py-1 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
                              >
                                {confirming === s.organization_id ? '처리 중…' : '이 요금으로 확인'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setOpenId(null)}
                                className="px-3 py-1 rounded-md border border-slate-300 text-slate-600"
                              >
                                취소
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openConfirm(s)}
                            className="mt-1 px-3 py-1 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700"
                          >
                            확인
                          </button>
                        )}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {s.agreed_by_name || '-'}
                    {s.agreed_by_email && (
                      <div className="text-xs text-slate-400">{s.agreed_by_email}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtKst(s.agreed_at)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {s.agreed_via || '-'}
                    {s.agreed_version && data.previous_versions?.includes(s.agreed_version) && (
                      <div className="text-slate-400">이전 판 동의</div>
                    )}
                    {s.agreed_ip && <div>{s.agreed_ip}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.owner_name || '-'}
                    {s.owner_phone && (
                      <div>
                        <a className="text-blue-600 underline" href={`tel:${s.owner_phone}`}>
                          {s.owner_phone}
                        </a>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HqSubscriptionConsentsPage;
